import { RouterProvider } from 'react-router-dom';
import { ThemeProvider, CssBaseline, createTheme } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { useMemo } from 'react';
import { muiThemeConfig } from '@/shared/theme/muiTheme';
import { light, dark } from '@/shared/theme/tokens';
import { useThemeStore } from './providers/themeStore';
import { QueryProvider } from './providers/QueryProvider';
import { AppBootGate } from './providers/AppBootGate';
import { DeploymentRoleProvider } from './providers/DeploymentRoleContext';
import { ErrorBoundary } from './ErrorBoundary';
import { router } from './routes/router';
import './styles/index.css';

function ThemedApp() {
  const effectiveMode = useThemeStore((s) => s.effectiveMode);
  const theme = useMemo(() => {
    // Make MUI's palette mode-aware for background/surfaces/text/divider so that
    // portalled MUI surfaces (picker calendars, Select/Autocomplete menus, dialogs)
    // render dark in dark mode. App surfaces mostly use token colors directly; this
    // wires the same tokens into MUI's own Paper/Menu defaults.
    const t = effectiveMode === 'dark' ? dark : light;
    return createTheme({
      ...muiThemeConfig,
      palette: {
        ...muiThemeConfig.palette,
        mode: effectiveMode,
        background: { default: t.bg, paper: t.bgSubtle },
        text: { primary: t.textPrimary, secondary: t.textMuted },
        divider: t.divider,
      },
    });
  }, [effectiveMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DeploymentRoleProvider>
          <RouterProvider router={router} />
        </DeploymentRoleProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <AppBootGate>
        <QueryProvider>
          <ThemedApp />
        </QueryProvider>
      </AppBootGate>
    </ErrorBoundary>
  );
}
